import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export const Card = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'rounded-xl border border-border bg-surface shadow-sm',
      className,
    )}
    {...p}
  />
);

export const CardHeader = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center justify-between gap-3 border-b border-border px-5 py-3.5', className)} {...p} />
);

export const CardTitle = ({ className, ...p }: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn('text-sm font-semibold tracking-tight text-fg', className)} {...p} />
);

export const CardBody = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5', className)} {...p} />
);

export type MetricVariant = 'blue' | 'emerald' | 'amber' | 'purple' | 'rose' | 'cyan' | 'neutral';

/** Colour only marks the icon; the card itself stays white so a row of figures reads as one block. */
const METRIC_VARIANTS: Record<MetricVariant, string> = {
  blue: 'bg-accent-soft text-accent',
  cyan: 'bg-info-soft text-info',
  purple: 'bg-accent-soft text-accent',
  emerald: 'bg-success-soft text-success',
  amber: 'bg-warning-soft text-warning',
  rose: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-muted',
};

export interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  value: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  icon?: ReactNode;
  variant?: MetricVariant;
}

export function MetricCard({
  title,
  value,
  subtitle,
  badge,
  icon,
  variant = 'blue',
  className,
  ...rest
}: MetricCardProps) {
  return (
    <div className={cn('rounded-xl border border-border bg-surface p-4 shadow-sm', className)} {...rest}>
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-sm font-medium text-muted">{title}</span>
        <div className="flex shrink-0 items-center gap-1.5">
          {badge}
          {icon && <div className={cn('flex size-8 items-center justify-center rounded-lg [&_svg]:size-4', METRIC_VARIANTS[variant] ?? METRIC_VARIANTS.blue)}>{icon}</div>}
        </div>
      </div>
      <div className="tnum mt-2 whitespace-nowrap text-2xl font-semibold tracking-tight text-fg">{value}</div>
      {subtitle && <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-muted">{subtitle}</div>}
    </div>
  );
}

const badge = cva('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium leading-5 whitespace-nowrap', {
  variants: {
    tone: {
      neutral: 'border-border bg-surface-2 text-muted',
      accent: 'border-accent/25 bg-accent-soft text-accent',
      success: 'border-success-border bg-success-soft text-success',
      warning: 'border-warning-border bg-warning-soft text-warning',
      danger: 'border-danger-border bg-danger-soft text-danger',
      info: 'border-info-border bg-info-soft text-info',
    },
  },
  defaultVariants: { tone: 'neutral' },
});
export type Tone = NonNullable<VariantProps<typeof badge>['tone']>;
export const Badge = ({ tone, className, children, dot }: { tone?: Tone; className?: string; children: ReactNode; dot?: boolean }) => (
  <span className={cn(badge({ tone }), className)}>{dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}{children}</span>
);

const STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral', SENT: 'info', ACCEPTED: 'success', REJECTED: 'danger', EXPIRED: 'neutral', CONFIRMED: 'info', POSTED: 'info', VALIDATED: 'accent', SIGNED: 'accent', SUBMITTED: 'info',
  ACCEPTED_BY_TTN: 'success', REJECTED_BY_TTN: 'danger', PARTIALLY_PAID: 'info', PAID: 'success', CREDITED: 'neutral', CANCELLED: 'neutral', UNPAID: 'warning', OVERDUE: 'danger', OVERPAID: 'warning',
  REVERSED: 'neutral', ACTIVE: 'success', PENDING: 'warning', REFUSED: 'danger',
};

/** One mapping from a backend status code to a localised label and a tone. The dot keeps colour from being the only signal. */
export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>{t(`status.${status}`, { defaultValue: status })}</Badge>;
}

export function PageHeader({ title, subtitle, actions, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {back}
        <h1 className="truncate text-xl font-semibold tracking-tight text-fg">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function DescriptionList({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((i) => (
        <div key={i.label} className="min-w-0">
          <dt className="text-xs font-medium text-muted">{i.label}</dt>
          <dd className="mt-1 break-words text-sm">{i.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
